'use client'
import { useCallback, useEffect, useState } from 'react'
import {
  EMPTY_CONTACT,
  clearContact as clearStore,
  isContactEmpty,
  markPaymentFailed as markStore,
  readContact,
  takePaymentFailed,
  writeContact,
  type BookingContact,
} from './bookingContactStorage'

/** sessionStorage, or null when unavailable (SSR, or a locked-down browser). */
function getStore(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

export type UseBookingContact = {
  contact: BookingContact
  setContact: React.Dispatch<React.SetStateAction<BookingContact>>
  /** True on a mount that follows a declined payment — drives the return banner. */
  returnedAfterFailure: boolean
  /** Record a Stripe decline so the next mount can explain the return. */
  markPaymentFailed: () => void
  /** Drop the saved contact + failure flag (call once the booking is confirmed). */
  clear: () => void
  /** Dismiss the return banner without clearing the saved details. */
  dismissReturnBanner: () => void
}

/**
 * Tenant-scoped booking-contact state backed by sessionStorage. Restores the
 * saved contact on mount and persists edits, so a card decline / reload / app
 * switch mid-checkout doesn't force a retype. See bookingContactStorage.ts for
 * the pure round-trip logic and why the scope is per-tenant.
 */
export function useBookingContact(tenantId: string): UseBookingContact {
  const [contact, setContact] = useState<BookingContact>(EMPTY_CONTACT)
  const [returnedAfterFailure, setReturnedAfterFailure] = useState(false)

  // Restore once on mount. sessionStorage is client-only, so this can't run
  // during SSR — the initial render matches the server's empty form, then this
  // effect fills it in post-hydration (no mismatch).
  useEffect(() => {
    const store = getStore()
    if (!store) return
    const saved = readContact(store, tenantId)
    if (saved) setContact(saved)
    if (takePaymentFailed(store, tenantId)) setReturnedAfterFailure(true)
  }, [tenantId])

  // Persist on change. The empty guard is load-bearing: on the mount commit this
  // effect runs with the initial empty contact BEFORE the restore re-render, and
  // writing empty would removeItem the value we're about to restore. Skipping
  // empties leaves the saved value intact; clear() is the only path that erases.
  useEffect(() => {
    if (isContactEmpty(contact)) return
    const store = getStore()
    if (store) writeContact(store, tenantId, contact)
  }, [contact, tenantId])

  const markPaymentFailed = useCallback(() => {
    const store = getStore()
    if (store) markStore(store, tenantId)
  }, [tenantId])

  const clear = useCallback(() => {
    setReturnedAfterFailure(false)
    const store = getStore()
    if (store) clearStore(store, tenantId)
  }, [tenantId])

  const dismissReturnBanner = useCallback(() => setReturnedAfterFailure(false), [])

  return { contact, setContact, returnedAfterFailure, markPaymentFailed, clear, dismissReturnBanner }
}
